// ═══════════════════════════════════════════════════════
// Schoolizy — Cycle Helpers UEMOA
// Gestion des 4 cycles scolaires et leurs spécificités
// ═══════════════════════════════════════════════════════

export const CYCLES = {
  PRESCOLAIRE:       'PRESCOLAIRE',
  ELEMENTAIRE:       'ELEMENTAIRE',
  POST_ELEMENTAIRE:  'POST_ELEMENTAIRE',
  SECONDAIRE:        'SECONDAIRE',
};

export const CYCLE_LABELS = {
  PRESCOLAIRE:      'Pré-Scolaire',
  ELEMENTAIRE:      'Élémentaire',
  POST_ELEMENTAIRE: 'Post-Élémentaire',
  SECONDAIRE:       'Secondaire',
};

export const CYCLE_COLORS = {
  PRESCOLAIRE:      '#f59e0b',
  ELEMENTAIRE:      '#3b82f6',
  POST_ELEMENTAIRE: '#8b5cf6',
  SECONDAIRE:       '#16a34a',
};

// ── Niveaux par cycle (UEMOA) ─────────────────────────
export const LEVELS_BY_CYCLE = {
  PRESCOLAIRE: [
    { code: 'PS',  label: 'Petite Section' },
    { code: 'MS',  label: 'Moyenne Section' },
    { code: 'GS',  label: 'Grande Section' },
  ],
  ELEMENTAIRE: [
    { code: 'CI',  label: 'Cours d\'Initiation' },
    { code: 'CP',  label: 'Cours Préparatoire' },
    { code: 'CE1', label: 'Cours Élémentaire 1' },
    { code: 'CE2', label: 'Cours Élémentaire 2' },
    { code: 'CM1', label: 'Cours Moyen 1' },
    { code: 'CM2', label: 'Cours Moyen 2' },
  ],
  POST_ELEMENTAIRE: [
    { code: '6e',  label: 'Sixième' },
    { code: '5e',  label: 'Cinquième' },
    { code: '4e',  label: 'Quatrième' },
    { code: '3e',  label: 'Troisième' },
  ],
  SECONDAIRE: [
    { code: '2nde', label: 'Seconde' },
    { code: '1ere', label: 'Première' },
    { code: 'Tle',  label: 'Terminale' },
  ],
};

// ── Domaines Pré-Scolaire (UEMOA) ────────────────────
export const DOMAINES_PRESCOLAIRE = [
  { code: 'LANGAGE',       label: 'Langage oral et communication' },
  { code: 'EVEIL_MATH',    label: 'Éveil aux mathématiques' },
  { code: 'EVEIL_SCI',     label: 'Éveil scientifique et technologique' },
  { code: 'MOTRICITE',     label: 'Motricité et psychomotricité' },
  { code: 'ARTISTIQUE',    label: 'Expression artistique et créativité' },
  { code: 'DEVELOPPEMENT', label: 'Développement personnel et social' },
  { code: 'LECTURE',       label: 'Pré-lecture et graphisme' },
];

// ── Appréciations Pré-Scolaire ────────────────────────
export const APPRECIATIONS_AEA = [
  { code: 'A',  label: 'Acquis',         color: '#16a34a', short: 'A' },
  { code: 'EA', label: 'En cours d\'Acquisition', color: '#f59e0b', short: 'EA' },
  { code: 'NA', label: 'Non Acquis',     color: '#dc2626', short: 'NA' },
];

// ── Appréciations notes (tout cycle) ─────────────────
export const APPRECIATION_NOTE = (note, bareme = 20) => {
  const sur20 = (note / bareme) * 20;
  if (sur20 >= 18) return 'Excellent';
  if (sur20 >= 16) return 'Très bien';
  if (sur20 >= 14) return 'Bien';
  if (sur20 >= 12) return 'Assez bien';
  if (sur20 >= 10) return 'Passable';
  if (sur20 >= 8)  return 'Insuffisant';
  return 'Très insuffisant';
};

// ── Barèmes par cycle et type d'évaluation ───────────
export const getBareme = (cycle, typeEval) => {
  if (cycle === CYCLES.PRESCOLAIRE) return null; // Pas de notes
  if (cycle === CYCLES.ELEMENTAIRE) {
    // Contrôle /10, Composition /20
    if (['COMPOSITION', 'EXAMEN_BLANC'].includes(typeEval)) return 20;
    return 10;
  }
  return 20; // Post-Élémentaire et Secondaire toujours /20
};

// ── Mode de présence par cycle ────────────────────────
export const getPresenceMode = (cycle) => {
  if (cycle === CYCLES.PRESCOLAIRE || cycle === CYCLES.ELEMENTAIRE) return 'DEMI_JOURNEE';
  return 'PAR_COURS';
};

// ── Détection du cycle depuis le nom de classe ou niveau ─
export const getCycleFromClassName = (name = '') => {
  const n = name.toUpperCase();
  if (['PS', 'MS', 'GS', 'PRÉ-SCOLAIRE', 'PRE-SCOLAIRE', 'MATERNELLE', 'PETITE', 'MOYENNE', 'GRANDE'].some((c) => n.includes(c))) return CYCLES.PRESCOLAIRE;
  if (['CI', 'CP', 'CE1', 'CE2', 'CM1', 'CM2', 'ÉLÉMENTAIRE', 'ELEMENTAIRE', 'INITIATION', 'PRÉPARATOIRE'].some((c) => n.includes(c))) return CYCLES.ELEMENTAIRE;
  if (['6E', '5E', '4E', '3E', 'SIXIÈME', 'CINQUIÈME', 'QUATRIÈME', 'TROISIÈME', 'POST'].some((c) => n.includes(c))) return CYCLES.POST_ELEMENTAIRE;
  if (['2NDE', 'SECONDE', '1ERE', '1ÈRE', 'PREMIÈRE', 'TLE', 'TERMINALE'].some((c) => n.includes(c))) return CYCLES.SECONDAIRE;
  return null;
};

// ── Détection du cycle depuis le nom du cycle Prisma ─────
// Normalise les accents pour la comparaison
function stripAccents(s) {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

export const getCycleFromCycleName = (cycleName = '') => {
  const n = stripAccents(cycleName.toUpperCase());
  if (n.includes('PRE') && n.includes('SCO')) return CYCLES.PRESCOLAIRE;
  if (n.includes('MATERNELLE')) return CYCLES.PRESCOLAIRE;
  // POST doit être testé AVANT ELEMENTAIRE
  if (n.includes('POST') || n.includes('COLLEGE') || n.includes('COLLEGE')) return CYCLES.POST_ELEMENTAIRE;
  if (n.includes('ELEMENTAIRE') || n.includes('PRIMAIRE')) return CYCLES.ELEMENTAIRE;
  if (n.includes('SECONDAIRE') || n.includes('LYCEE')) return CYCLES.SECONDAIRE;
  return null;
};

// ── Types d'évaluation par cycle ─────────────────────
export const EVAL_TYPES_BY_CYCLE = {
  PRESCOLAIRE: [], // Pas d'évaluations notées — seulement des observations
  ELEMENTAIRE: [
    { v: 'CONTROLE',    l: 'Contrôle',    bareme: 10 },
    { v: 'COMPOSITION', l: 'Composition', bareme: 20 },
    { v: 'DICTEE',      l: 'Dictée',      bareme: 10 },
    { v: 'EXERCICE',    l: 'Exercice',    bareme: 10 },
  ],
  POST_ELEMENTAIRE: [
    { v: 'CONTROLE',     l: 'Contrôle',     bareme: 20 },
    { v: 'DEVOIR',       l: 'Devoir',       bareme: 20 },
    { v: 'COMPOSITION',  l: 'Composition',  bareme: 20 },
    { v: 'EXAMEN_BLANC', l: 'Examen blanc', bareme: 20 },
  ],
  SECONDAIRE: [
    { v: 'CONTROLE',     l: 'Contrôle',     bareme: 20 },
    { v: 'DEVOIR',       l: 'Devoir',       bareme: 20 },
    { v: 'COMPOSITION',  l: 'Composition',  bareme: 20 },
    { v: 'EXAMEN_BLANC', l: 'Examen blanc', bareme: 20 },
    { v: 'BAC_BLANC',    l: 'Bac blanc',    bareme: 20 },
  ],
};

// ── Périodes/Trimestres par cycle ─────────────────────
// Année scolaire UEMOA : Oct → Juil
// T1: Oct–Jan | T2: Fév–Avr | T3: Mai–Juil
export const TRIMESTRES = [
  { num: 1, label: 'Trimestre 1', from: '10', to: '01' },
  { num: 2, label: 'Trimestre 2', from: '02', to: '04' },
  { num: 3, label: 'Trimestre 3', from: '05', to: '07' },
];

export const getTrimestreFromDate = (dateStr) => {
  const month = new Date(dateStr + 'T12:00:00').getMonth() + 1;
  if (month >= 10 || month === 1) return 1;
  if (month >= 2 && month <= 4)   return 2;
  if (month >= 5 && month <= 7)   return 3;
  return 1;
};

// ── Coefficients par défaut (Secondaire) ─────────────
export const COEFFICIENTS_SECONDAIRE = {
  'Français':          3,
  'Mathématiques':     4,
  'Sciences':          2,
  'Histoire-Géo':      2,
  'Anglais':           2,
  'Philosophie':       2,
  'EPS':               1,
  'Éducation civique': 1,
};

// ── Mention bac (Secondaire) ──────────────────────────
export const getMentionBac = (moyenne) => {
  const m = Number(moyenne);
  if (m >= 16) return { label: 'Mention Très Bien', color: '#16a34a' };
  if (m >= 14) return { label: 'Mention Bien',       color: '#22c55e' };
  if (m >= 12) return { label: 'Mention Assez Bien', color: '#84cc16' };
  if (m >= 10) return { label: 'Passable',           color: '#f97316' };
  return              { label: 'Ajourné',             color: '#dc2626' };
};
